import type { FC, ReactNode } from 'react'
import type { BoardPiece } from '@/lib/chess'

import { useEffect, useMemo, useRef, useState } from 'react'

import {
    Crown,
    Pause,
    Play,
    RotateCcw,
    SkipBack,
    SkipForward
} from 'lucide-react'

import { cn } from '@/lib/utils'
import NeonBoard from '@/components/chess/neon-board'
import NeonPiece from '@/components/chess/neon-piece'
import { useBoardTheme } from '@/components/chess/board-theme'
import { buildReplay, findKing } from '@/lib/chess'
import { IMMORTAL_GAME } from '@/constants/games'

const CAPTURE_ORDER: Record<string, number> = { q: 0, r: 1, b: 2, n: 3, p: 4, k: 5 }

interface PlayerStripProps {
    name: string
    sideLabel: string
    color: string
    capturedColor: string
    captured: BoardPiece[]
    toMove: boolean
    winner: boolean
}

const PlayerStrip: FC<PlayerStripProps> = ({
    name,
    sideLabel,
    color,
    capturedColor,
    captured,
    toMove,
    winner
}): ReactNode => {
    return (
        <div className='flex items-center justify-between gap-3 px-1'>
            <div className='flex min-w-0 items-center gap-2.5'>
                <span
                    className='flex size-8 shrink-0 items-center justify-center rounded-lg border bg-black/40 transition-shadow duration-500'
                    style={{ borderColor: `${color}66`, color, boxShadow: toMove ? `0 0 16px ${color}99` : 'none' }}
                >
                    <NeonPiece type='k' strokeWidth={2} className='size-5' />
                </span>

                <div className='min-w-0'>
                    <p className='truncate text-sm font-semibold text-foreground'>
                        {name}
                    </p>

                    <p className='font-mono text-[11px] text-muted-foreground'>
                        {sideLabel}
                        {toMove && <span style={{ color }}> · to move</span>}
                    </p>
                </div>
            </div>

            <div className='flex min-w-0 items-center gap-2'>
                <div className='flex max-w-[8.5rem] flex-wrap justify-end gap-y-0.5 -space-x-1' style={{ color: capturedColor }}>
                    {captured.map(piece => (
                        <NeonPiece key={piece.id} type={piece.type} strokeWidth={2.2} className='size-4 opacity-80' />
                    ))}
                </div>

                {winner && (
                    <span className='flex shrink-0 items-center gap-1 font-mono text-xs font-semibold' style={{ color }}>
                        <Crown className='size-4' />
                        1-0
                    </span>
                )}
            </div>
        </div>
    )
}

const HeroReplay: FC = (): ReactNode => {
    const { theme } = useBoardTheme()
    const replay = useMemo(() => buildReplay(IMMORTAL_GAME.moves), [])
    const total = replay.steps.length

    const [ply, setPly] = useState(0)
    const [playing, setPlaying] = useState(true)
    const [inView, setInView] = useState(true)
    const [reducedMotion, setReducedMotion] = useState(false)

    const rootRef = useRef<HTMLDivElement>(null)
    const stripRef = useRef<HTMLOListElement>(null)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setReducedMotion(true)
            setPlaying(false)
        }
    }, [])

    useEffect(() => {
        const node = rootRef.current
        if (!node || typeof IntersectionObserver === 'undefined') return

        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.25 })
        observer.observe(node)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!playing || !inView || total === 0) return

        const delay = ply >= total ? 4500 : ply === 0 ? 1100 : 1450
        const timer = window.setTimeout(() => setPly(current => (current >= total ? 0 : current + 1)), delay)

        return () => window.clearTimeout(timer)
    }, [playing, inView, ply, total])

    useEffect(() => {
        const strip = stripRef.current
        if (!strip) return

        const active = strip.querySelector<HTMLElement>('[data-active="true"]')
        const left = active ? active.offsetLeft - strip.clientWidth / 2 + active.clientWidth / 2 : 0

        strip.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' })
    }, [ply, reducedMotion])

    const step = ply > 0 ? replay.steps[ply - 1] : null
    const pieces = step ? step.pieces : replay.initial
    const lastMove = step ? { from: step.move.from, to: step.move.to } : null
    const checkSquare = step?.check ? findKing(step.pieces, step.move.color === 'w' ? 'b' : 'w') : null
    const finished = Boolean(step?.mate)
    const whiteToMove = !finished && ply % 2 === 0

    const capturedFrom = (color: 'w' | 'b') => pieces
        .filter(piece => piece.captured && piece.color === color)
        .sort((first, second) => CAPTURE_ORDER[first.type] - CAPTURE_ORDER[second.type])

    const moveLabel = step
        ? `${Math.ceil(ply / 2)}${step.move.color === 'w' ? '.' : '...'} ${step.move.san}`
        : 'Starting position'

    const note = IMMORTAL_GAME.notes[ply] ?? (step ? `${IMMORTAL_GAME.white} vs. ${IMMORTAL_GAME.black}` : IMMORTAL_GAME.opening)

    const jumpTo = (target: number) => {
        setPlaying(false)
        setPly(Math.min(total, Math.max(0, target)))
    }

    const togglePlay = () => {
        if (!playing && ply >= total) setPly(0)
        setPlaying(value => !value)
    }

    const controlClass = 'inline-flex size-9 items-center justify-center rounded-full border border-white/10 text-muted-foreground transition-colors hover:border-white/25 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan disabled:pointer-events-none disabled:opacity-35'

    return (
        <div
            ref={rootRef}
            className='relative rounded-[1.75rem] border border-white/10 bg-[#080817]/80 p-3.5 shadow-[0_30px_80px_-20px_rgb(0_0_0/0.8)] backdrop-blur-md sm:p-5'
        >
            <div className='mb-4 flex flex-wrap items-center justify-between gap-2 px-1'>
                <span className='inline-flex items-center gap-2 rounded-full border border-neon-lime/35 bg-neon-lime/10 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-neon-lime'>
                    <span className='relative flex size-1.5'>
                        <span className='absolute inset-0 rounded-full bg-neon-lime animate-neon-ping' />
                        <span className='relative size-1.5 rounded-full bg-neon-lime' />
                    </span>
                    Live replay
                </span>

                <p className='font-mono text-[11px] text-muted-foreground'>
                    {IMMORTAL_GAME.title} · {IMMORTAL_GAME.place}
                </p>
            </div>

            <PlayerStrip
                name={IMMORTAL_GAME.black}
                sideLabel='Black'
                color={theme.black}
                capturedColor={theme.white}
                captured={capturedFrom('w')}
                toMove={!finished && !whiteToMove}
                winner={false}
            />

            <NeonBoard
                pieces={pieces}
                theme={theme}
                label={`${IMMORTAL_GAME.title} replay, ${moveLabel}`}
                lastMove={lastMove}
                checkSquare={checkSquare}
                className='my-3'
            />

            <PlayerStrip
                name={IMMORTAL_GAME.white}
                sideLabel='White'
                color={theme.white}
                capturedColor={theme.black}
                captured={capturedFrom('b')}
                toMove={whiteToMove}
                winner={finished}
            />

            <div className='mt-4 h-1 overflow-hidden rounded-full bg-white/5'>
                <div
                    className='h-full rounded-full transition-[width] duration-500'
                    style={{
                        width: `${total ? (ply / total) * 100 : 0}%`,
                        background: `linear-gradient(90deg, ${theme.white}, ${theme.black})`,
                        boxShadow: `0 0 12px ${theme.white}99`
                    }}
                />
            </div>

            <div className='mt-4 flex items-center justify-between gap-3 px-1'>
                <div className='min-w-0'>
                    <p
                        className='font-mono text-sm font-semibold'
                        style={{ color: finished ? '#b6ff3b' : theme.white, textShadow: `0 0 12px ${finished ? '#b6ff3b' : theme.white}80` }}
                    >
                        {moveLabel}
                    </p>

                    <p className='truncate text-xs text-muted-foreground'>
                        {note}
                    </p>
                </div>

                <div className='flex shrink-0 items-center gap-1.5'>
                    <button type='button' aria-label='Restart replay' onClick={() => jumpTo(0)} className={controlClass}>
                        <RotateCcw className='size-4' />
                    </button>

                    <button type='button' aria-label='Previous move' onClick={() => jumpTo(ply - 1)} disabled={ply === 0} className={controlClass}>
                        <SkipBack className='size-4' />
                    </button>

                    <button
                        type='button'
                        aria-label={playing ? 'Pause replay' : 'Play replay'}
                        onClick={togglePlay}
                        className='inline-flex size-10 items-center justify-center rounded-full text-primary-foreground transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'
                        style={{ backgroundColor: theme.white, boxShadow: `0 0 18px ${theme.white}99` }}
                    >
                        {playing ? <Pause className='size-4' /> : <Play className='size-4 translate-x-px' />}
                    </button>

                    <button type='button' aria-label='Next move' onClick={() => jumpTo(ply + 1)} disabled={ply >= total} className={controlClass}>
                        <SkipForward className='size-4' />
                    </button>
                </div>
            </div>

            <ol
                ref={stripRef}
                aria-label='Moves of the game'
                className='no-scrollbar relative mt-4 flex items-center gap-0.5 overflow-x-auto border-t border-white/5 px-1 pt-3'
            >
                {replay.steps.map((entry, index) => {
                    const active = ply === index + 1

                    return (
                        <li key={`${index}-${entry.move.san}`} className='flex shrink-0 items-center'>
                            {entry.move.color === 'w' && (
                                <span className='ml-1.5 mr-0.5 font-mono text-[11px] text-muted-foreground/60'>
                                    {Math.floor(index / 2) + 1}.
                                </span>
                            )}

                            <button
                                type='button'
                                data-active={active}
                                aria-current={active ? 'step' : undefined}
                                onClick={() => jumpTo(index + 1)}
                                className={cn(
                                    'rounded-md px-1.5 py-1 font-mono text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan',
                                    active ? 'bg-white/10 font-semibold' : 'text-muted-foreground hover:text-foreground'
                                )}
                                style={active ? { color: theme.white, textShadow: `0 0 10px ${theme.white}` } : undefined}
                            >
                                {entry.move.san}
                            </button>
                        </li>
                    )
                })}
            </ol>
        </div>
    )
}

export default HeroReplay
